/**
 * DamageFloaters — Spawn a "-N" floater at the projected screen position
 * of the victim drone whenever a damage event lands. Floaters drift up and
 * fade over ~0.7s, then remove themselves via animationend.
 *
 * DOM-only (no WebGL): an absolute-positioned div per floater, projected
 * by THREE.Camera.project() each spawn. We don't reposition floaters every
 * frame — once placed, the CSS keyframe owns their motion.
 */
import * as THREE from 'three/webgpu';
import type { ClientNetManager } from '../networking/ClientNetManager';
import { DroneType, ServerEvent } from '../../shared/types';
import { shouldIgnoreDamageEvent } from '../game/eventGating';
import type { Vec3 } from '../../shared/utils';

export interface DamageFloatersGateState {
  localSessionId: string;
  localDroneIds: ReadonlySet<string>;
}

/**
 * Resolve an attacker drone id back to its {@link DroneType}. Returns
 * `undefined` for environmental damage / unknown ids — the floater then
 * skips the icon prefix and source-tint instead of crashing.
 */
export type AttackerDroneTypeLookup = (attackerDroneId: string) => DroneType | undefined;

/**
 * Per-drone-type icon glyph rendered as a prefix on each floater. Pulled
 * from widely-supported emoji so the prefix renders the same on every
 * browser without custom assets. Healer is included because heal-as-damage
 * abilities (e.g. `ConvertShot`) can route through a Healer attacker.
 */
export const DRONE_TYPE_FLOATER_ICONS: Record<DroneType, string> = {
  [DroneType.Striker]: '⚡',
  [DroneType.Sniper]: '🎯',
  [DroneType.Healer]: '✚',
  [DroneType.Interceptor]: '➤',
  [DroneType.Support]: '◆',
  [DroneType.Guardian]: '🛡',
};

/**
 * Source-accent tint class — layered alongside the magnitude/crit class
 * so each layer can be styled independently. The contrast guard (text
 * shadow on `.damage-floater`) still wins for readability.
 */
export const DRONE_TYPE_FLOATER_TINT_CLASS: Record<DroneType, string> = {
  [DroneType.Striker]: 'dmg-floater--src-striker',
  [DroneType.Sniper]: 'dmg-floater--src-sniper',
  [DroneType.Healer]: 'dmg-floater--src-healer',
  [DroneType.Interceptor]: 'dmg-floater--src-interceptor',
  [DroneType.Support]: 'dmg-floater--src-support',
  [DroneType.Guardian]: 'dmg-floater--src-guardian',
};

/**
 * Optional multiplier breakdown attached to a damage event. Mirrors the
 * server-side `DamageDealtEvent.multipliers` shape (U8). Only entries
 * meaningfully ≠ 1 are sent; missing keys are treated as a 1× no-op.
 */
export interface DamageMultiplierBreakdown {
  crit?: number;
  focusFire?: number;
  supportAura?: number;
  syncDive?: number;
  defensiveWall?: number;
  falloff?: number;
}

function makeFloaterChild(
  tag: 'span',
  className: string,
  text: string,
  ariaHidden = false,
): HTMLElement {
  const el = document.createElement(tag);
  el.className = className;
  el.textContent = text;
  if (ariaHidden) el.setAttribute('aria-hidden', 'true');
  return el;
}

const FLOATER_LIFETIME_MS = 700;
// Crit floaters live longer than normal floaters so the "✦N" reads as a
// distinct beat — not just a re-skin of the standard damage tick.
const CRIT_FLOATER_LIFETIME_MS = 1100;
const FLOATER_CLEANUP_PAD_MS = 200;
const MAX_ACTIVE_FLOATERS = 24;

/** Lifetime (ms) the CSS animation `damageFloaterRise` runs for a crit
 * floater. Exported so tests can assert the animation timing field
 * without importing the (DOM-touching) DamageFloaters class. */
export const CRIT_FLOATER_LIFETIME = CRIT_FLOATER_LIFETIME_MS;
/** Lifetime (ms) for a normal (non-crit) floater. */
export const NORMAL_FLOATER_LIFETIME = FLOATER_LIFETIME_MS;

/**
 * Pick a severity class + label for a damage floater.
 *
 * Magnitude buckets: low (<10), mid (>=10), high (>=25). A crit overrides
 * the magnitude bucket and uses the special prefixed label. `isCrit` is
 * intentionally optional — populated on the event payload by the server;
 * until then `undefined` falls through to the magnitude branches.
 */
export function classifyDamageFloater(
  damage: number,
  isCrit: boolean | undefined,
): { className: string; label: string } {
  if (isCrit) return { className: 'damage-floater dmg-floater--crit', label: `✦${damage}` };
  if (damage >= 25) return { className: 'damage-floater dmg-floater--high', label: `-${damage}` };
  if (damage >= 10) return { className: 'damage-floater dmg-floater--mid', label: `-${damage}` };
  return { className: 'damage-floater dmg-floater--low', label: `-${damage}` };
}

export interface FloaterSpec {
  className: string;
  label: string;
  /** Lifetime in ms used both for the CSS animation timing and the JS
   * cleanup-fallback timeout. Crits live longer than normal floaters. */
  lifetimeMs: number;
  /** When true, DamageFloaters appends a `dmg-floater-crit-sparkle` div
   * inside the floater. Pure flag so tests can assert without touching
   * the DOM. */
  sparkle: boolean;
  /** Icon glyph (emoji) prefix derived from the attacker's drone type.
   * Empty string when the attacker is unknown / environmental — callers
   * MUST treat empty as "no icon" and skip the DOM prefix entirely. */
  iconPrefix: string;
  /** Multiplier-breakdown badge text rendered below the damage number on
   * crit floaters (e.g. `×1.5`, `×1.5×2`). Empty string when no badge
   * should be rendered (no meaningful multipliers attached). */
  multiplierBadge: string;
  /** Dominant tint class for the multiplier badge — `--crit` (gold) wins
   * when a crit is present, `--aura` (blue) wins when supportAura is
   * present without crit, otherwise empty (neutral). The DamageFloaters
   * DOM write applies this alongside the base `dmg-floater-mult-badge`
   * class so CSS handles the color swap. */
  multiplierBadgeTint: '' | 'dmg-floater--multiplier-badge--crit' | 'dmg-floater--multiplier-badge--aura';
  /** True when this floater represents a missed shot. The `damage` value
   * is irrelevant in this case — the floater renders the gray "MISS"
   * variant with no number and no badge. */
  missed: boolean;
}

/**
 * Format a multiplier-breakdown badge for a damage floater. On a crit
 * the leading 1.5x crit slot is always rendered, plus any meaningful
 * (≠ 1) buff multipliers chained after. On a non-crit hit the badge is
 * rendered only when a supportAura buff is in play — that's the one
 * non-crit multiplier the player benefits from seeing surfaced (blue
 * badge). Other non-crit multipliers (focusFire, falloff, etc.) collapse
 * into the headline damage number.
 *
 * Order: crit → focusFire → supportAura → syncDive → defensiveWall →
 * falloff.
 */
export function buildMultiplierBadge(
  isCrit: boolean,
  breakdown: DamageMultiplierBreakdown | undefined,
): string {
  // Non-crit hits only surface a badge when a supportAura buff is
  // active — anything else is too noisy to read in a 700ms floater.
  const auraActive = !!breakdown?.supportAura && breakdown.supportAura !== 1;
  if (!isCrit && !auraActive) return '';
  const parts: string[] = [];
  if (isCrit) {
    parts.push(`×${formatMultiplier(breakdown?.crit ?? 1.5)}`);
  }
  // Order matters — pinned by tests. New keys append to the tail.
  const tail: Array<keyof DamageMultiplierBreakdown> = [
    'focusFire',
    'supportAura',
    'syncDive',
    'defensiveWall',
    'falloff',
  ];
  for (const key of tail) {
    const v = breakdown?.[key];
    // On a non-crit hit only the supportAura entry survives — focusFire /
    // syncDive / defensiveWall / falloff are dropped to keep the badge
    // legible (their effect is folded into the headline number).
    if (!isCrit && key !== 'supportAura') continue;
    if (typeof v === 'number' && v !== 1) {
      parts.push(`×${formatMultiplier(v)}`);
    }
  }
  return parts.join('');
}

/**
 * Pick the dominant tint class for the multiplier badge: gold for crit,
 * blue for supportAura, empty (neutral) otherwise. Crit always wins —
 * a critting aura-buffed hit reads as a crit, not an aura tick.
 */
export function pickMultiplierBadgeTint(
  isCrit: boolean,
  breakdown: DamageMultiplierBreakdown | undefined,
): FloaterSpec['multiplierBadgeTint'] {
  if (isCrit) return 'dmg-floater--multiplier-badge--crit';
  if (breakdown?.supportAura && breakdown.supportAura !== 1) {
    return 'dmg-floater--multiplier-badge--aura';
  }
  return '';
}

function formatMultiplier(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}` : `${rounded.toFixed(1)}`;
}

/**
 * Read an optional `multipliers` object off a damage event payload. The
 * server today does not send this field; we extract defensively so the
 * floater stays robust against schema additions without requiring a
 * coordinated client-side bump.
 */
export function extractMultiplierBreakdown(
  data: unknown,
): DamageMultiplierBreakdown | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const m = (data as { multipliers?: unknown }).multipliers;
  if (!m || typeof m !== 'object') return undefined;
  const src = m as Record<string, unknown>;
  const out: DamageMultiplierBreakdown = {};
  if (typeof src.crit === 'number') out.crit = src.crit;
  if (typeof src.focusFire === 'number') out.focusFire = src.focusFire;
  if (typeof src.supportAura === 'number') out.supportAura = src.supportAura;
  if (typeof src.syncDive === 'number') out.syncDive = src.syncDive;
  if (typeof src.defensiveWall === 'number') out.defensiveWall = src.defensiveWall;
  if (typeof src.falloff === 'number') out.falloff = src.falloff;
  return out;
}

/**
 * Pure helper that returns everything {@link DamageFloaters.spawnFloater}
 * needs to render a single floater. Splitting the spec out from the DOM
 * write keeps the crit/normal distinction unit-testable without a jsdom
 * environment (the rest of the project tests run DOM-free).
 *
 * Unknown attacker (`attackerDroneType === undefined`) intentionally
 * skips both the source-tint class and the icon — environmental damage
 * and late state-sync stay readable instead of crashing.
 */
export function buildFloaterSpec(
  damage: number,
  isCrit: boolean | undefined,
  attackerDroneType?: DroneType,
  multipliers?: DamageMultiplierBreakdown,
  missed?: boolean,
): FloaterSpec {
  // MISS variant short-circuits the magnitude / crit / multiplier paths
  // entirely. The floater renders gray "MISS" text with no number and
  // no badge — the dedicated `--miss` modifier owns its own keyframe.
  if (missed === true) {
    return {
      className: 'damage-floater dmg-floater--miss',
      label: 'MISS',
      lifetimeMs: FLOATER_LIFETIME_MS,
      sparkle: false,
      iconPrefix: '',
      multiplierBadge: '',
      multiplierBadgeTint: '',
      missed: true,
    };
  }
  const { className, label } = classifyDamageFloater(damage, isCrit);
  const crit = isCrit === true;
  const knownType = typeof attackerDroneType === 'number';
  const sourceTintClass = knownType ? DRONE_TYPE_FLOATER_TINT_CLASS[attackerDroneType] ?? '' : '';
  const iconPrefix = knownType ? DRONE_TYPE_FLOATER_ICONS[attackerDroneType] ?? '' : '';
  return {
    className: sourceTintClass ? `${className} ${sourceTintClass}` : className,
    label,
    lifetimeMs: crit ? CRIT_FLOATER_LIFETIME_MS : FLOATER_LIFETIME_MS,
    sparkle: crit,
    iconPrefix,
    multiplierBadge: buildMultiplierBadge(crit, multipliers),
    multiplierBadgeTint: pickMultiplierBadgeTint(crit, multipliers),
    missed: false,
  };
}

export class DamageFloaters {
  private layer: HTMLElement;
  private camera: THREE.Camera;
  private getGateState: () => DamageFloatersGateState;
  /**
   * Resolve attacker drone id → DroneType from network state. Optional so
   * tests / callers without room state still work; defaults to a no-op
   * that returns undefined (floater then skips icon + source-tint).
   */
  private lookupAttackerDroneType: AttackerDroneTypeLookup;
  private attached = false;
  private active: HTMLElement[] = [];
  private projectVec = new THREE.Vector3();

  constructor(
    layer: HTMLElement,
    camera: THREE.Camera,
    getGateState: () => DamageFloatersGateState,
    lookupAttackerDroneType?: AttackerDroneTypeLookup,
  ) {
    this.layer = layer;
    this.camera = camera;
    this.getGateState = getGateState;
    this.lookupAttackerDroneType = lookupAttackerDroneType ?? (() => undefined);
  }

  attach(net: ClientNetManager): void {
    if (this.attached) return;
    this.attached = true;
    net.onServerEvent((eventType, data) => {
      if (!this.attached) return;
      if (eventType !== ServerEvent.DamageDealt) return;
      // MISS events ship with damage=0, which the shared
      // `shouldIgnoreDamageEvent` gate (rightly) drops for HitMarkers /
      // DamageDirection — a whiff has no impact-flash or screen-edge
      // arrow. The floater layer is the sole place we *want* to surface
      // a miss, so we let `missed === true` through that gate locally.
      const isMiss = (data as { missed?: unknown }).missed === true;
      if (!isMiss && shouldIgnoreDamageEvent(data)) return;
      const pos = data?.position as Vec3 | undefined;
      if (!pos) return;
      // Only show numbers when our team is involved — surfacing every distant
      // duel between AI/other players would clutter the screen.
      const gate = this.getGateState();
      const attackerId = typeof data.attackerDroneId === 'string' ? data.attackerDroneId : '';
      const targetId = typeof data.targetDroneId === 'string' ? data.targetDroneId : '';
      const localAttacker = gate.localDroneIds.has(attackerId);
      const localTarget = gate.localDroneIds.has(targetId);
      if (!localAttacker && !localTarget) return;
      const damage = typeof data.damage === 'number' ? data.damage : 0;
      const isCrit = typeof data.isCrit === 'boolean' ? data.isCrit : undefined;
      // Unknown attacker (env damage, late state-sync) → undefined →
      // floater quietly drops both the icon and source-tint layers.
      const attackerType = attackerId ? this.lookupAttackerDroneType(attackerId) : undefined;
      const multipliers = extractMultiplierBreakdown(data);
      const missed = (data as { missed?: unknown }).missed === true;
      // Friendly fire / self-damage: when both attacker and target are local
      // drones the hit is fundamentally an OUTGOING action (the player chose
      // to fire), so the incoming-direction styling would mislead. Treating
      // it as outgoing keeps the floater consistent with the player's input.
      const isOnLocal = localTarget && !localAttacker;
      this.spawnFloater(
        pos,
        Math.round(damage),
        isOnLocal,
        isCrit,
        attackerType,
        multipliers,
        missed,
      );
    });
  }

  detach(): void {
    this.attached = false;
    for (const el of this.active) el.remove();
    this.active = [];
  }

  /** No-op: floaters animate via CSS. Kept for renderer-pattern symmetry. */
  update(_deltaMs: number): void {}

  private spawnFloater(
    world: Vec3,
    amount: number,
    isOnLocal: boolean,
    isCrit: boolean | undefined,
    attackerDroneType?: DroneType,
    multipliers?: DamageMultiplierBreakdown,
    missed?: boolean,
  ): void {
    const screen = this.worldToScreen(world);
    if (!screen) return;
    if (this.active.length >= MAX_ACTIVE_FLOATERS) {
      this.active.shift()?.remove();
    }

    const spec = buildFloaterSpec(amount, isCrit, attackerDroneType, multipliers, missed);
    const direction = isOnLocal ? 'incoming' : 'outgoing';
    const el = document.createElement('div');
    el.className = `${spec.className} ${direction}`;
    if (spec.missed) {
      // MISS variant: gray "MISS" text only, no icon, no number, no badge.
      el.appendChild(makeFloaterChild('span', 'dmg-floater-number', spec.label));
    } else {
      // Structured children let CSS size the icon, number, and badge
      // independently — the headline number stays dominant.
      if (spec.iconPrefix) {
        el.appendChild(makeFloaterChild('span', 'dmg-floater-icon', spec.iconPrefix, true));
      }
      el.appendChild(makeFloaterChild('span', 'dmg-floater-number', spec.label));
      if (spec.multiplierBadge) {
        // Badge tint (gold for crit, blue for supportAura, neutral) layers
        // on top of the base `dmg-floater-mult-badge` class so CSS owns
        // the color swap without per-element inline styles.
        const badgeClass = spec.multiplierBadgeTint
          ? `dmg-floater-mult-badge ${spec.multiplierBadgeTint}`
          : 'dmg-floater-mult-badge';
        el.appendChild(makeFloaterChild('span', badgeClass, spec.multiplierBadge, true));
      }
      // Crit floaters get a sparkle child so the "✦N" reads as a distinct
      // beat. The `.dmg-floater--crit` selector handles the longer keyframe
      // and gold palette; the JS-side timeout below honors `spec.lifetimeMs`.
      if (spec.sparkle) {
        const sparkle = document.createElement('div');
        sparkle.className = 'dmg-floater-crit-sparkle';
        sparkle.setAttribute('aria-hidden', 'true');
        el.appendChild(sparkle);
      }
    }
    el.style.left = `${screen.x}px`;
    el.style.top = `${screen.y}px`;
    this.layer.appendChild(el);
    this.active.push(el);
    this.scheduleFloaterCleanup(el, spec.lifetimeMs);
  }

  /**
   * Wire up the dual-cleanup path (animationend + timeout fallback) for a
   * floater element. Shared between MISS and normal paths so both code
   * paths get the same idempotency + listener-removal contract.
   */
  private scheduleFloaterCleanup(el: HTMLElement, lifetimeMs: number): void {
    let removed = false;
    const cleanup = () => {
      if (removed) return;
      removed = true;
      el.removeEventListener('animationend', cleanup);
      const idx = this.active.indexOf(el);
      if (idx >= 0) this.active.splice(idx, 1);
      el.remove();
    };
    el.addEventListener('animationend', cleanup);
    // Fallback in case animationend never fires (tab hidden, animation aborted, etc.).
    setTimeout(cleanup, lifetimeMs + FLOATER_CLEANUP_PAD_MS);
  }

  /**
   * Project a world point to viewport pixels. Returns null when the point
   * is behind the camera (z > 1 in NDC) or outside the viewport's NDC box
   * — an off-screen target should not consume a slot in MAX_ACTIVE_FLOATERS
   * and evict on-screen floaters.
   */
  private worldToScreen(world: Vec3): { x: number; y: number } | null {
    const v = this.projectVec.set(world.x, world.y, world.z).project(this.camera);
    if (v.z > 1 || Math.abs(v.x) > 1 || Math.abs(v.y) > 1) return null;
    const w = this.layer.clientWidth || window.innerWidth;
    const h = this.layer.clientHeight || window.innerHeight;
    return {
      x: (v.x * 0.5 + 0.5) * w,
      y: (-v.y * 0.5 + 0.5) * h,
    };
  }
}
